import { CaseStatus } from './caseStatus'

export interface ClientDashboardItem {
  id: number
  caseType: string
  lastVisitedDate: string | null
  status: string
  caseNumber: string | null
  attorneyName: string | null
  firmName: string | null
  openDate: string | null
}

export interface Firm {
  id: number
  name: string
  officeId: string | null
  emailAddress: string | null
  iconUrl: string | null
  street: string | null
  city: string | null
  state: string | null
  zip: string | null
  phone: string | null
  fax: string | null
  website: string | null
}

export interface Matter {
  id: number
  bccReference: number
  caseNumber: string | null
  active: boolean
  attorneyId: number
  attorneyName: string | null
  attorneyAvatarUrl: string
  caseType: string
  lastVisitedDate: string | null
  status: string
  openDate: string | null
  statuses: CaseStatus[] | null | undefined
  firm: Firm
  isJoint: boolean | null
  debtor1FirstCourseForJointCaseOrders: number | null
  debtor1SecondCourseForJointCaseOrders: number | null
  debtor1EmailAddress: string | null
  debtor1FirstName: string | null
  debtor1LastName: string | null
  debtor2EmailAddress: string | null
  debtor2FirstName: string | null
  debtor2LastName: string | null
  isAiEnabled: boolean
  isChatBotEnabled: boolean
  isCompleteAllChecklistUploadedDocuments: boolean
  hasLegacyOrder: boolean
  clientCode: string | null
  organizationalUnitIdKey: number | null
  debtor1HasPlacedFirstCourseOrder: boolean
  debtor1HasPlacedSecondCourseOrder: boolean
  debtor1HasPlacedFirstCourseOrderStatus: number
  debtor1HasPlacedSecondCourseOrderStatus: number
  debtor1HasPlacedFirstCourseOrderStatusMessage: string | null
  debtor1HasPlacedSecondCourseOrderStatusMessage: string | null
  debtor1FirstCourseOrderStep: number | null
  debtor1SecondCourseOrderStep: number | null
  debtor2FirstCourseForJointCaseOrders: number | null
  debtor2SecondCourseForJointCaseOrders: number | null
  debtor2HasPlacedFirstCourseOrder: boolean
  debtor2HasPlacedSecondCourseOrder: boolean
  debtor2HasPlacedFirstCourseOrderStatus: number
  debtor2HasPlacedSecondCourseOrderStatus: number
  debtor2HasPlacedFirstCourseOrderStatusMessage: string | null
  debtor2HasPlacedSecondCourseOrderStatusMessage: string | null
  debtor2FirstCourseOrderStep: number | null
  debtor2SecondCourseOrderStep: number | null
  questionnaireDisplayInClientPortalFlag: boolean
  questionnaireExpectedCompletionDate: string | null
  questionnaireExpectedCompletionMonth: string | null
  questionnaireExpectedCompletionDay: string | null
  questionnaireExpectedCompletionYear: string | null
  questionnaireExpectedCompletionFormatted: string | null
  questionnaireInstructions: string | null
  questionnaireShownInstructionsFlag: boolean
  questionnaireShownTermsAndConditionsFlag: boolean
  dashBoardTitle: string | null
  dashBoardHeaderDescription: string | null
  dashBoardCurrentStatusDescription: string | null
  dashBoardShowSubmitDocumentsFlag: boolean | null
  dashBoardCourseNumber: string | null
  orderItemSourceTypeId: number | null
}

export interface SectionDataRecord {
  id: number
  sectionId: number
  questionKey: string
  questionDataJsonText: string
  questionCompleteFlag: boolean
}

export interface Section {
  id: number
  sectionSystemName: string
  sectionName: string
  sectionStatus: number
  sectionDescription: string
  sectionOrder: number
  version: number
  comments: string | null
  currentActiveQuestionId: number | null
  currentActiveGroup: number | null
  isRejected: boolean
  sectionDataRecords: SectionDataRecord[]
  // eslint-disable-next-line
  sectionUiTransformedModel: any | null
  sectionCardSummary: string | null
  isDisplayEnabled: boolean
}

export enum MatterClientPortalQuestionnaireSectionStatus {
  NotStarted = 1,
  InProgress = 2,
  Completed = 3,
  Submitted = 4,
  Rejected = 5,
  Imported = 6
}

export function getQuestionnaireSectionStatusName(
  status: MatterClientPortalQuestionnaireSectionStatus
): string {
  if (status === MatterClientPortalQuestionnaireSectionStatus.NotStarted) {
    return 'Not Started'
  } else if (
    status === MatterClientPortalQuestionnaireSectionStatus.InProgress
  ) {
    return 'In Progress'
  } else if (
    status === MatterClientPortalQuestionnaireSectionStatus.Completed
  ) {
    return 'Complete'
  } else if (
    status === MatterClientPortalQuestionnaireSectionStatus.Submitted
  ) {
    return 'Complete'
  } else if (status === MatterClientPortalQuestionnaireSectionStatus.Rejected) {
    return 'In Progress'
  } else if (status === MatterClientPortalQuestionnaireSectionStatus.Imported) {
    return 'Complete'
  }

  return 'Unknown'
}

export function getSectionButtonText(
  status: MatterClientPortalQuestionnaireSectionStatus
): string {
  let sectionButtonText = ''
  switch (status) {
    case MatterClientPortalQuestionnaireSectionStatus.NotStarted:
      sectionButtonText = 'Start Section'
      break
    case MatterClientPortalQuestionnaireSectionStatus.InProgress:
      sectionButtonText = 'Resume Section'
      break
    case MatterClientPortalQuestionnaireSectionStatus.Completed:
      sectionButtonText = 'Edit Section'
      break
    case MatterClientPortalQuestionnaireSectionStatus.Rejected:
      sectionButtonText = 'Resume Section'
      break
    case MatterClientPortalQuestionnaireSectionStatus.Submitted:
      sectionButtonText = 'View Section'
      break
    default:
      sectionButtonText = ''
  }
  return sectionButtonText
}

export function getIconByStatus(
  status: MatterClientPortalQuestionnaireSectionStatus
): string {
  let iconClass = ''
  switch (status) {
    case MatterClientPortalQuestionnaireSectionStatus.NotStarted:
      iconClass = ''
      break
    case MatterClientPortalQuestionnaireSectionStatus.InProgress:
      iconClass = 'mdi mdi-clock-outline'
      break
    case MatterClientPortalQuestionnaireSectionStatus.Completed:
      iconClass = 'mdi mdi-check-circle-outline'
      break
    case MatterClientPortalQuestionnaireSectionStatus.Submitted:
      iconClass = 'mdi mdi-check-circle-outline'
      break
    case MatterClientPortalQuestionnaireSectionStatus.Rejected:
      iconClass = 'mdi mdi-clock-outline'
      break
    default:
      iconClass = ''
  }
  return iconClass
}

export enum HasPlacedCourseOrderStatusType {
  NoOrder = 1,
  PlaceCourseOrderInProgress = 2,
  HasOrder = 3,
  ErrorMultipleOrdersFoundError = 4,
  ErrorOrderWithDifferentLastNameFoundError = 5,
  ErrorMissingDudiUserError = 6,
  ErrorMissingOrganizationalUnitError = 7,
  ErrorUnknown = 8,
  ErrorOnLookupStepUserVerification = 9,
  ErrorOnCreateAccountStep = 10,
  ErrorOnAddSpouseorJointDebtorStep = 11,
  ErrorOnChooseCourseStep = 12,
  ErrorOnPlaceAttorneyConsumerOrderStep = 13
}

export interface CasePlaceOrderUpdateFields {
  isDebtor1: boolean
  hasPlacedFirstCourseOrder: boolean | null
  hasPlacedSecondCourseOrder: boolean | null
  hasPlacedFirstCourseOrderStatus: number | null
  hasPlacedSecondCourseOrderStatus: number | null
  firstCourseOrderStep: number | null
  secondCourseOrderStep: number | null
  orderItemSourceTypeId: number | null
}

export interface CaseForJointCaseOrderUpdateFields {
  isDebtor1: boolean
  firstCourseForJointCaseOrders: number | null
  secondCourseForJointCaseOrders: number | null
}

export enum CourseOrderStep {
  UserVerification = 1,
  CreateAccount = 2,
  AddSpouseOrJointDebtor = 3,
  ChooseCourse = 4,
  CourseOrderedSuccessfully = 5,
  CourseOrderFailed = 6,
  LookupFoundCourse = 7
}

export function getCasePlaceOrderUpdateFields(
  isDebtor1: boolean,
  isFirstCourse: boolean,
  courseOrderStatus: HasPlacedCourseOrderStatusType,
  courseOrderStep: CourseOrderStep
): CasePlaceOrderUpdateFields {
  const casePlaceOrderUpdateFields: CasePlaceOrderUpdateFields = {
    isDebtor1: isDebtor1,
    hasPlacedFirstCourseOrder: null,
    hasPlacedSecondCourseOrder: null,
    hasPlacedFirstCourseOrderStatus: isFirstCourse ? courseOrderStatus : null,
    hasPlacedSecondCourseOrderStatus: isFirstCourse ? null : courseOrderStatus,
    firstCourseOrderStep: isFirstCourse ? courseOrderStep : null,
    secondCourseOrderStep: isFirstCourse ? null : courseOrderStep,
    orderItemSourceTypeId: null
  }

  return casePlaceOrderUpdateFields
}
